import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import './AddDeviceModal.css';

const AddDeviceModal = ({ onClose, onAdd }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [serialNumber, setSerialNumber] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [deviceTypeValue, setDeviceTypeValue] = useState('');
  const [isDeviceNameEdited, setIsDeviceNameEdited] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [parentSerial, setParentSerial] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmIsError, setConfirmIsError] = useState(false);

  // Time-based access state
  const [showTimeAccess, setShowTimeAccess] = useState(false);
  const [selectedForTimeAccess, setSelectedForTimeAccess] = useState([]);
  const [userTimeSettings, setUserTimeSettings] = useState({}); // Store individual time settings per user

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const res = await apiClient.get(`/users`);
        const users = (res.data.users || []).map(({ user, device }) => ({
          UserId: user.UserId,
          Name: user.Name,
          Phone: user.Phone,
          Devices: device || []
        }));
        setAllUsers(users);
      } catch (error) {
        console.error(error);
      }
    };
    fetchAllUsers();
  }, []);

  useEffect(() => {
    const prefix = serialNumber.slice(0, 5).toUpperCase();
    let newLabel = '';
    let newType = '';
    switch (prefix) {
      case 'IL_IB':
        newLabel = 'iBeam';
        newType = 'beam';
        break;
      case 'IL_BX':
        newLabel = 'iLock Blue';
        newType = 'lock';
        break;
      case 'IL_LA':
        newLabel = 'Latch';
        newType = 'latch';
        break;
      default:
        newLabel = '';
        newType = '';
    }
    if (!isDeviceNameEdited) {
      setDeviceName(newLabel);
    }
    setDeviceType(newLabel);
    setDeviceTypeValue(newType);
  }, [serialNumber]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSuggestions([]);
      return;
    }
    const q = searchTerm.toLowerCase();
    setSuggestions(
      allUsers.filter(u =>
        (u.Name.toLowerCase().includes(q) || u.Phone.includes(q)) &&
        !selectedUsers.some(su => su.UserId === u.UserId)
      )
    );
  }, [searchTerm, allUsers, selectedUsers]);

  const handleUserSelect = user => {
    setSelectedUsers(us => [...us, user]);
    setSearchTerm('');
    setSuggestions([]);
  };

  const handleRemoveUser = UserId => {
    setSelectedUsers(us => us.filter(u => u.UserId !== UserId));
    // Also remove from time access selection and settings
    setSelectedForTimeAccess(prev => prev.filter(id => id !== UserId));
    setUserTimeSettings(prev => {
      const newSettings = { ...prev };
      delete newSettings[UserId];
      return newSettings;
    });
  };

  const handleTimeAccessToggle = (userId) => {
    setSelectedForTimeAccess(prev => {
      const isCurrentlySelected = prev.includes(userId);
      if (isCurrentlySelected) {
        // Remove from selection and clear settings
        setUserTimeSettings(prevSettings => {
          const newSettings = { ...prevSettings };
          delete newSettings[userId];
          return newSettings;
        });
        return prev.filter(id => id !== userId);
      } else {
        // Add to selection with default times
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextWeek = new Date(now);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        setUserTimeSettings(prevSettings => ({
          ...prevSettings,
          [userId]: {
            startDate: tomorrow.toISOString().slice(0, 16),
            endDate: nextWeek.toISOString().slice(0, 16)
          }
        }));
        return [...prev, userId];
      }
    });
  };

  const updateUserTimeSettings = (userId, field, value) => {
    setUserTimeSettings(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value
      }
    }));
  };

  const getUserAccessType = (userId) => {
    if (!showTimeAccess) return 'permanent';
    if (!selectedForTimeAccess.includes(userId)) return 'permanent';
    
    // Check if it's pending (future start date)
    const timeSettings = getUserTimeSettings(userId);
    if (timeSettings.startDate && timeSettings.endDate) {
      const now = new Date();
      const startDate = new Date(timeSettings.startDate);
      if (startDate > now) return 'pending';
    }
    
    return 'temporary';
  };

  const getUserTimeSettings = (userId) => {
    return userTimeSettings[userId] || { startDate: '', endDate: '' };
  };

  const handleAddClick = () => {
    if (!serialNumber.trim()) {
      setConfirmMessage('Serial number is required.');
      setConfirmIsError(true);
      setShowConfirmModal(true);
      return;
    }
    if (!deviceType) {
      setConfirmMessage('Could not determine device type from serial number.');
      setConfirmIsError(true);
      setShowConfirmModal(true);
      return;
    }
    if (parentSerial.trim()) {
      const parentPrefix = parentSerial.slice(0, 5).toUpperCase();
      if (parentPrefix !== 'IL_IB') {
        setConfirmMessage('Only iBeam can be a parent device.');
        setConfirmIsError(true);
        setShowConfirmModal(true);
        return;
      }
      if (deviceTypeValue !== 'latch' && deviceTypeValue !== 'lock') {
        setConfirmMessage('Only latch and lock can be sub-devices.');
        setConfirmIsError(true);
        setShowConfirmModal(true);
        return;
      }
    }

    // Validate time-based access for each selected user
    if (showTimeAccess && selectedForTimeAccess.length > 0) {
      for (const userId of selectedForTimeAccess) {
        const timeSettings = getUserTimeSettings(userId);
        if (!timeSettings.startDate || !timeSettings.endDate) {
          setConfirmMessage('Start and end dates are required for all users with time-based access.');
          setConfirmIsError(true);
          setShowConfirmModal(true);
          return;
        }
        if (new Date(timeSettings.endDate) <= new Date(timeSettings.startDate)) {
          setConfirmMessage('End date must be after start date for all users.');
          setConfirmIsError(true);
          setShowConfirmModal(true);
          return;
        }
        if (new Date(timeSettings.startDate) < new Date()) {
          setConfirmMessage('Start date cannot be in the past for any user.');
          setConfirmIsError(true);
          setShowConfirmModal(true);
          return;
        }
      }
    }

    // Prepare users with access type information
    const usersWithAccess = selectedUsers.map(user => {
      const accessType = getUserAccessType(user.UserId);
      const timeSettings = getUserTimeSettings(user.UserId);
      return {
        ...user,
        accessType,
        IsDelegate: accessType === 'temporary' || accessType === 'pending',
        startDate: (accessType === 'temporary' || accessType === 'pending') ? timeSettings.startDate : null,
        endDate: (accessType === 'temporary' || accessType === 'pending') ? timeSettings.endDate : null
      };
    });

    onAdd(
      usersWithAccess,
      serialNumber.trim(),
      deviceName.trim(),
      deviceTypeValue,
      parentSerial.trim()
    );
    setConfirmMessage('Device added successfully!');
    setConfirmIsError(false);
    setShowConfirmModal(true);
  };

  return (
    <>
      <div className="add-device-backdrop">
        <div className="add-device-modal">
          <h2>Add Device</h2>

          <div className="add-device-input-group">
            <label className="add-device-label">Serial Number</label>
            <input
              type="text"
              value={serialNumber}
              onChange={e => setSerialNumber(e.target.value)}
              className="add-device-input"
              placeholder="Enter serial number…"
            />
          </div>

          <div className="add-device-input-group">
            <label className="add-device-label">Device Type</label>
            <input
              type="text"
              value={deviceType}
              readOnly
              className="add-device-input"
              placeholder="Auto-detected from serial…"
            />
          </div>

          <div className="add-device-input-group">
            <label className="add-device-label">Device Name</label>
            <input
              type="text"
              value={deviceName}
              onChange={e => {
                setDeviceName(e.target.value);
                setIsDeviceNameEdited(true);
              }}
              className="add-device-input"
              placeholder="Device Name"
            />
          </div>

          <div className="add-device-input-group">
            <label className="add-device-label">Select User (optional)</label>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="add-device-input"
              placeholder="Search by name or phone…"
            />
            {suggestions.length > 0 && (
              <ul className="add-device-suggestions">
                {suggestions.map(sug => (
                  <li
                    key={sug.UserId}
                    className="add-device-suggestion-item"
                    onClick={() => handleUserSelect(sug)}
                  >
                    {sug.Name} ({sug.Phone})
                  </li>
                ))}
              </ul>
            )}

            {selectedUsers.length > 0 && (
              <div className="selected-users">
                {selectedUsers.map(u => {
                  const accessType = getUserAccessType(u.UserId);
                  return (
                    <span
                      key={u.UserId}
                      className={`user-tag ${accessType === 'permanent' ? 'permanent-access' : accessType === 'temporary' ? 'temporary-access' : 'pending-access'}`}
                    >
                      <span className="access-icon">
                        {accessType === 'permanent' && 'PERM'}
                        {accessType === 'temporary' && 'TEMP'}
                        {accessType === 'pending' && 'PEND'}
                      </span>
                      {u.Name} ({u.Phone})
                      {(accessType === 'temporary' || accessType === 'pending') && (() => {
                        const timeSettings = getUserTimeSettings(u.UserId);
                        return timeSettings.startDate && timeSettings.endDate && (
                          <span className="date-range">
                            • {new Date(timeSettings.startDate).toLocaleDateString()} - {new Date(timeSettings.endDate).toLocaleDateString()}
                          </span>
                        );
                      })()}
                      {accessType === 'pending' && <span className="status-badge pending">PENDING</span>}
                      <button
                        type="button"
                        onClick={() => handleRemoveUser(u.UserId)}
                        className="remove-user-button"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {selectedUsers.length > 0 && (
              <div className="time-access-section">
                <label className="time-access-toggle">
                  <input
                    type="checkbox"
                    checked={showTimeAccess}
                    onChange={(e) => {
                      setShowTimeAccess(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedForTimeAccess([]);
                        setUserTimeSettings({});
                      }
                    }}
                  />
                  Enable time-based access
                </label>

                {showTimeAccess && (
                  <div className="time-access-controls">
                    <div className="user-selection-for-time">
                      <label className="add-device-label">Configure time-based access for users:</label>
                      <div className="user-time-settings">
                        {selectedUsers.map(u => (
                          <div key={u.UserId} className="user-time-setting">
                            <label className="user-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedForTimeAccess.includes(u.UserId)}
                                onChange={() => handleTimeAccessToggle(u.UserId)}
                              />
                              <span className="user-name">{u.Name} ({u.Phone})</span>
                            </label>
                            
                            {selectedForTimeAccess.includes(u.UserId) && (
                              <div className="individual-date-inputs">
                                <div className="date-input-group">
                                  <label className="date-label">Start Date & Time</label>
                                  <input
                                    type="datetime-local"
                                    value={getUserTimeSettings(u.UserId).startDate}
                                    onChange={(e) => updateUserTimeSettings(u.UserId, 'startDate', e.target.value)}
                                    className="date-input"
                                    min={new Date().toISOString().slice(0, 16)}
                                  />
                                </div>
                                <div className="date-input-group">
                                  <label className="date-label">End Date & Time</label>
                                  <input
                                    type="datetime-local"
                                    value={getUserTimeSettings(u.UserId).endDate}
                                    onChange={(e) => updateUserTimeSettings(u.UserId, 'endDate', e.target.value)}
                                    className="date-input"
                                    min={getUserTimeSettings(u.UserId).startDate || new Date().toISOString().slice(0, 16)}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="add-device-input-group">
            <label className="add-device-label">Parent Serial Number (optional)</label>
            <input
              type="text"
              value={parentSerial}
              onChange={e => setParentSerial(e.target.value)}
              className="add-device-input"
              placeholder="If this is a sub-device…"
            />
          </div>

          <div className="add-device-actions">
            <button onClick={onClose} className="add-device-button">
              Cancel
            </button>
            <button onClick={handleAddClick} className="add-device-button">
              Add Device
            </button>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={confirmMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={confirmIsError}
          onConfirm={() => {
            setShowConfirmModal(false);
            if (!confirmIsError) {
              onClose();
            }
          }}
        />
      )}
    </>
  );
};

export default AddDeviceModal;